import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { ConfirmDialog } from '@/shared/components/custom/confirm-dialog/ConfirmDialog';
import { useConfirm } from '@/shared/hooks/useConfirm';
import { useInstantFormat } from '@/shared/hooks/useInstantFormat';
import { useTaskTimeEntriesInfinite } from '../hooks/useTimeEntriesInfinite';
import { useDeleteTimeEntry } from '../hooks/useManualEntryMutations';
import { formatTrackedHours } from '../utils/formatTrackedHours';
import { TimeEntryFormDialog } from './TimeEntryFormDialog';
import type { TimeEntryDto } from '../types';

interface TaskTimeEntriesSectionProps {
  taskId: string;
}

export function TaskTimeEntriesSection({ taskId }: TaskTimeEntriesSectionProps) {
  const { t } = useTranslation('timeTracking');
  const formatInstant = useInstantFormat();
  const { entries, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useTaskTimeEntriesInfinite(taskId);
  const deleteEntry = useDeleteTimeEntry(taskId);
  const { confirm, isOpen, options, handleConfirm, handleCancel } = useConfirm();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<TimeEntryDto | undefined>(undefined);

  async function handleDelete(entry: TimeEntryDto) {
    if (!entry.id) return;
    const result = await confirm({
      title: t('entries.confirmDelete.title'),
      description: t('entries.confirmDelete.description'),
      confirmLabel: t('entries.confirmDelete.confirm'),
      cancelLabel: t('entries.confirmDelete.cancel'),
      variant: 'destructive',
    });
    if (result.confirmed) deleteEntry.mutate(entry.id);
  }

  return (
    <section data-testid="timetracking-entries-section" aria-label={t('entries.title')}>
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-medium">{t('entries.title')}</h3>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            setEditing(undefined);
            setDialogOpen(true);
          }}
          data-testid="timetracking-entry-add-btn"
        >
          <Plus className="mr-1.5 size-4" />
          {t('entries.add')}
        </Button>
      </div>

      {isLoading && (
        <div className="flex flex-col gap-2" data-testid="timetracking-entries-loading">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      )}

      {!isLoading && entries.length === 0 && (
        <p className="py-2 text-xs text-muted-foreground" data-testid="timetracking-entries-empty">
          {t('entries.empty')}
        </p>
      )}

      {!isLoading && entries.length > 0 && (
        <div className="divide-y divide-border/50 overflow-hidden rounded-md border border-border/50">
          {entries.map((entry) => (
            <div
              key={entry.id}
              className="group/entry flex items-center gap-3 px-3 py-2"
              data-testid="timetracking-entry-row"
            >
              <span className="w-16 shrink-0 text-sm font-medium tabular-nums">
                {formatTrackedHours(entry.durationHours)}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs text-muted-foreground">
                  {formatInstant(entry.startTime, { showTime: true })}
                  {' – '}
                  {entry.endTime
                    ? formatInstant(entry.endTime, { showTime: true })
                    : t('entries.inProgress')}
                </p>
                {entry.description && <p className="truncate text-sm">{entry.description}</p>}
              </div>
              {entry.isBillable && (
                <Badge variant="secondary" className="shrink-0">
                  {t('entries.billable')}
                </Badge>
              )}

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="size-7 opacity-0 transition-opacity group-hover/entry:opacity-100"
                    onClick={() => {
                      setEditing(entry);
                      setDialogOpen(true);
                    }}
                    aria-label={t('entries.edit')}
                    data-testid="timetracking-entry-edit-btn"
                  >
                    <Pencil className="size-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t('entries.edit')}</TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="size-7 text-destructive opacity-0 transition-opacity hover:text-destructive group-hover/entry:opacity-100"
                    onClick={() => handleDelete(entry)}
                    aria-label={t('entries.delete')}
                    data-testid="timetracking-entry-delete-btn"
                  >
                    <Trash2 className="size-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t('entries.delete')}</TooltipContent>
              </Tooltip>
            </div>
          ))}

          {hasNextPage && (
            <button
              type="button"
              onClick={fetchNextPage}
              disabled={isFetchingNextPage}
              className="flex w-full items-center justify-center px-3 py-2 text-xs text-muted-foreground transition-colors hover:bg-accent/30 hover:text-foreground disabled:opacity-50"
              data-testid="timetracking-entries-load-more"
            >
              {isFetchingNextPage ? t('entries.loading') : t('entries.loadMore')}
            </button>
          )}
        </div>
      )}

      {dialogOpen && (
        <TimeEntryFormDialog
          taskId={taskId}
          entry={editing}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
        />
      )}

      {isOpen && options && (
        <ConfirmDialog
          open={isOpen}
          onOpenChange={(v) => !v && handleCancel()}
          title={options.title}
          description={options.description}
          confirmLabel={options.confirmLabel}
          cancelLabel={options.cancelLabel}
          variant={options.variant}
          onConfirm={handleConfirm}
          data-testid="timetracking-entry-delete-confirm"
        />
      )}
    </section>
  );
}
